/**
 * A session's full bar skeleton for lightweight-charts: one slot per bar from
 * the session's first bar to its last, at the chart's bar width, with the real
 * bars merged in.
 *
 * A slot with no bar is whitespace (`{ time }` and no OHLC): the time scale
 * reserves it and labels it, nothing is drawn there, and it carries no price,
 * so autoscaling never sees it. A replay stopped at 10:25 therefore shows its
 * morning bars at a normal width with the rest of the day blank, instead of
 * twelve bars stretched across the pane.
 *
 * The bounds come from the chart endpoint (`first_bar_ts` / `last_bar_ts`,
 * from shared/market_calendar.py), never from a close time assumed here: a
 * half day ends at 13:00 and must not get three hours of phantom slots.
 *
 * Pure: no chart, no DOM.
 */

/** The raw epoch (seconds) of every slot of a session, oldest first. */
export function sessionSlots(session, intervalMinutes) {
  const first = Number(session && session.first_bar_ts);
  const last = Number(session && session.last_bar_ts);
  const step = Math.max(1, Number(intervalMinutes) || 1) * 60;
  if (!Number.isFinite(first) || !Number.isFinite(last) || last < first) return [];
  const out = [];
  for (let t = first; t <= last; t += step) out.push(t);
  return out;
}

/**
 * The candle series' data: the skeleton of every given session with the real
 * bars in their slots, sorted by time.
 *
 * @param slotTimes  chart-time slot times (whitespace where no bar sits)
 * @param bars       real bars in chart time, `{ time, open, high, low, close }`
 * @returns          [{ time } | { time, open, high, low, close }]
 */
export function mergeSkeleton(slotTimes, bars) {
  const points = new Map();
  for (const time of slotTimes || []) points.set(time, { time });
  // A bar off the grid (an odd provider alignment) is kept, never dropped.
  for (const { time, open, high, low, close } of bars || []) points.set(time, { time, open, high, low, close });
  return [...points.values()].sort((a, b) => a.time - b.time);
}
